/**
 * FormsBuilderSection
 * -------------------
 * Custom lead-capture forms builder voor de Instellingen > Formulier tab.
 *
 * Functionaliteit:
 *  - Lijst van bestaande formulieren met active-toggle, embed URL, edit/delete.
 *  - Modal voor maken/bewerken met live veld-editor.
 *  - Per veld: key, label, type (text/email/phone/textarea/number/date/url/select/multi_select/radio/checkbox),
 *    required, placeholder, help text, options (voor select/radio/multi).
 *  - Per formulier instelbaar: lead_source, lead_stage, lead_tag, redirect_url,
 *    success_message, accent_color, show_logo, notify_emails, assigned_to.
 *  - Embed URL + iframe snippet kopiëren.
 */
import { useEffect, useMemo, useState } from 'react';
import { formsBuilderAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus, Edit2, Trash2, Copy, Check, Eye, ExternalLink, Mail, Tag,
  Send, Settings as SettingsIcon, FormInput, GripVertical, ChevronDown, ChevronUp,
  AlertCircle, Layers, Loader2, Power,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text',        label: 'Tekst' },
  { value: 'email',       label: 'E-mail' },
  { value: 'phone',       label: 'Telefoon' },
  { value: 'textarea',    label: 'Lange tekst' },
  { value: 'number',      label: 'Getal' },
  { value: 'date',        label: 'Datum' },
  { value: 'url',         label: 'URL' },
  { value: 'select',      label: 'Dropdown' },
  { value: 'multi_select',label: 'Meerkeuze (chips)' },
  { value: 'radio',       label: 'Radio buttons' },
  { value: 'checkbox',    label: 'Checkbox' },
];

const TYPES_WITH_OPTIONS = new Set(['select', 'multi_select', 'radio']);

function slugify(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function keyify(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9]+/, '')
    .slice(0, 60) || 'field';
}

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  intro_text: '',
  submit_button_label: 'Verzenden',
  fields: [],
  active: true,
  lead_source: '',
  lead_stage: 'nieuw',
  lead_tag: '',
  assigned_to: null,
  notify_emails: [],
  redirect_url: '',
  success_message: 'Bedankt! We hebben je inzending ontvangen.',
  accent_color: '#F97316',
  show_logo: true,
};

export default function FormsBuilderSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState('');
  const [editing, setEditing] = useState(null); // null | {form, isNew}

  const reload = async () => {
    setLoading(true);
    try {
      const res = await formsBuilderAPI.adminList();
      setForms(res.data?.forms || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon formulieren niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleNew = () => {
    setEditing({ form: { ...EMPTY_FORM }, isNew: true });
  };

  const handleEdit = (form) => {
    setEditing({ form: { ...EMPTY_FORM, ...form }, isNew: false });
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Formulier "${form.name}" verwijderen?`)) return;
    try {
      await formsBuilderAPI.adminDelete(form.id);
      toast.success('Formulier verwijderd');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const handleToggleActive = async (form) => {
    try {
      await formsBuilderAPI.adminUpdate(form.id, { active: !form.active });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon status niet wijzigen');
    }
  };

  const handleCopy = (text, slug) => {
    navigator.clipboard.writeText(text);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(''), 1500);
  };

  return (
    <div className="space-y-4">
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FormInput className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm">Forms Builder</CardTitle>
              <Badge variant="secondary" className="text-[10px]">{forms.length} formulier{forms.length === 1 ? '' : 'en'}</Badge>
            </div>
            {isAdmin && (
              <Button size="sm" className="gap-1.5 h-8" onClick={handleNew} data-testid="forms-new-btn">
                <Plus className="w-3.5 h-3.5" /> Nieuw formulier
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Bouw custom lead-capture formulieren en zet ze aan/uit. Elk formulier krijgt een eigen publieke URL en kan ingesloten worden op je website.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-border rounded-lg">
              <FormInput className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Nog geen formulieren.</p>
              {isAdmin && (
                <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={handleNew}>
                  <Plus className="w-3.5 h-3.5" /> Eerste formulier maken
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {forms.map(f => (
                <FormCard
                  key={f.id}
                  form={f}
                  isAdmin={isAdmin}
                  copiedSlug={copiedSlug}
                  onCopy={handleCopy}
                  onEdit={() => handleEdit(f)}
                  onDelete={() => handleDelete(f)}
                  onToggleActive={() => handleToggleActive(f)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <FormBuilderModal
          initial={editing.form}
          isNew={editing.isNew}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

// ─── Form card ───
function FormCard({ form, isAdmin, copiedSlug, onCopy, onEdit, onDelete, onToggleActive }) {
  const [expanded, setExpanded] = useState(false);
  const publicUrl = `${window.location.origin}/forms/${form.slug}`;
  const iframeSnippet = `<iframe src="${publicUrl}" width="100%" height="720" frameborder="0" style="border:0;border-radius:12px;"></iframe>`;
  const copied = copiedSlug === form.slug;
  const copiedIframe = copiedSlug === `${form.slug}__iframe`;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-3 bg-card">
        <div
          className="w-1.5 h-10 rounded-full flex-shrink-0"
          style={{ backgroundColor: form.accent_color || '#F97316' }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate" data-testid={`form-name-${form.slug}`}>{form.name}</span>
            <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">/forms/{form.slug}</code>
            {form.active ? (
              <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-300/40">Actief</Badge>
            ) : (
              <Badge variant="secondary" className="text-[10px]">Uitgeschakeld</Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Layers className="w-3 h-3" />{(form.fields || []).length} veld{(form.fields || []).length === 1 ? '' : 'en'}</span>
            <span className="flex items-center gap-1"><Send className="w-3 h-3" />{form.submission_count || 0} inzending{(form.submission_count || 0) === 1 ? '' : 'en'}</span>
            {form.lead_source && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />bron: <code className="font-mono">{form.lead_source}</code></span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Switch
            checked={!!form.active}
            onCheckedChange={onToggleActive}
            disabled={!isAdmin}
            data-testid={`form-toggle-${form.slug}`}
          />
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setExpanded(e => !e)} data-testid={`form-expand-${form.slug}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          {isAdmin && (
            <>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} data-testid={`form-edit-${form.slug}`}>
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={onDelete} data-testid={`form-delete-${form.slug}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
      {expanded && (
        <div className="p-3 bg-muted/40 border-t space-y-3">
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium text-muted-foreground">Publieke URL</p>
            <div className="flex gap-2">
              <Input readOnly value={publicUrl} className="h-8 text-xs font-mono flex-1" />
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => onCopy(publicUrl, form.slug)}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" asChild>
                <a href={publicUrl} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /> Open</a>
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium text-muted-foreground">Iframe embed snippet</p>
            <div className="flex gap-2">
              <Textarea readOnly value={iframeSnippet} rows={2} className="text-[11px] font-mono flex-1 resize-none" />
              <Button size="sm" variant="outline" className="h-8 gap-1.5 self-start" onClick={() => onCopy(iframeSnippet, `${form.slug}__iframe`)}>
                {copiedIframe ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedIframe ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal ───
function FormBuilderModal({ initial, isNew, onClose, onSaved }) {
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basic'); // basic | fields | submit | branding
  const [autoSlug, setAutoSlug] = useState(isNew); // auto-sync slug zolang user 'm niet zelf aanpast

  const set = (patch) => setData(p => ({ ...p, ...patch }));

  // auto-slug from name
  useEffect(() => {
    if (autoSlug && isNew) {
      set({ slug: slugify(data.name) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.name]);

  const onSlugChange = (v) => {
    setAutoSlug(false);
    set({ slug: slugify(v) });
  };

  const handleSave = async () => {
    // Basis validatie
    if (!data.name?.trim()) { toast.error('Naam is vereist'); setTab('basic'); return; }
    if (!data.slug?.trim()) { toast.error('Slug is vereist'); setTab('basic'); return; }
    if (!data.fields || data.fields.length === 0) {
      toast.error('Voeg minstens één veld toe');
      setTab('fields');
      return;
    }
    const keys = data.fields.map(f => f.key);
    if (new Set(keys).size !== keys.length) {
      toast.error('Veldsleutels moeten uniek zijn');
      setTab('fields');
      return;
    }
    const hasEmail = data.fields.some(f => f.type === 'email' || (f.key || '').toLowerCase().includes('email') || (f.key || '').toLowerCase().includes('mail'));
    if (!hasEmail) {
      toast.error('Voeg een e-mail veld toe — anders kan er geen lead worden aangemaakt');
      setTab('fields');
      return;
    }

    setSaving(true);
    try {
      // Strip lege options voor non-option fields, en clean defaults
      const cleanFields = data.fields.map(f => ({
        ...f,
        options: TYPES_WITH_OPTIONS.has(f.type) ? (f.options || []).filter(o => (o.label || '').trim()) : [],
      }));
      const payload = {
        ...data,
        fields: cleanFields,
        notify_emails: (data.notify_emails || []).filter(e => (e || '').trim()),
        lead_source: (data.lead_source || '').trim() || `form_${data.slug}`,
      };

      if (isNew) {
        await formsBuilderAPI.adminCreate(payload);
        toast.success('Formulier aangemaakt');
      } else {
        await formsBuilderAPI.adminUpdate(initial.id, payload);
        toast.success('Formulier opgeslagen');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  // Field helpers
  const addField = (type = 'text') => {
    const base = {
      text: { label: 'Veld', key: `field_${(data.fields || []).length + 1}` },
      email: { label: 'E-mailadres', key: 'email' },
      phone: { label: 'Telefoonnummer', key: 'phone' },
      textarea: { label: 'Opmerking', key: 'message' },
      number: { label: 'Getal', key: 'number_field' },
      date: { label: 'Datum', key: 'date_field' },
      url: { label: 'URL', key: 'url_field' },
      select: { label: 'Keuze', key: 'choice' },
      multi_select: { label: 'Selectie', key: 'tags' },
      radio: { label: 'Optie', key: 'option' },
      checkbox: { label: 'Akkoord', key: 'consent' },
    }[type] || { label: 'Veld', key: `field_${(data.fields || []).length + 1}` };

    const newField = {
      key: keyify(base.key),
      label: base.label,
      type,
      required: type === 'email',
      placeholder: '',
      help_text: '',
      default_value: null,
      options: TYPES_WITH_OPTIONS.has(type) ? [{ label: 'Optie 1', value: 'option_1' }] : [],
    };
    set({ fields: [...(data.fields || []), newField] });
  };

  const updateField = (idx, patch) => {
    const next = [...data.fields];
    next[idx] = { ...next[idx], ...patch };
    set({ fields: next });
  };
  const removeField = (idx) => set({ fields: data.fields.filter((_, i) => i !== idx) });
  const moveField = (idx, dir) => {
    const next = [...data.fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    set({ fields: next });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="form-builder-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Nieuw formulier' : `Bewerk: ${data.name}`}</DialogTitle>
        </DialogHeader>

        {/* Mini-tabs */}
        <div className="flex border-b -mx-6 px-6 -mt-2">
          {[
            { k: 'basic', label: 'Basis', icon: SettingsIcon },
            { k: 'fields', label: `Velden (${(data.fields || []).length})`, icon: Layers },
            { k: 'submit', label: 'Submit-acties', icon: Send },
            { k: 'branding', label: 'Branding', icon: Eye },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors gap-1 flex items-center ${
                  tab === t.k ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`form-tab-${t.k}`}
              >
                <Icon className="w-3.5 h-3.5 mr-1" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Tab: Basic */}
        {tab === 'basic' && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Naam *</Label>
                <Input
                  value={data.name || ''}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="bijv. Nieuwsbrief Aanmelden"
                  data-testid="form-input-name"
                />
              </div>
              <div>
                <Label className="text-xs">URL slug *</Label>
                <Input
                  value={data.slug || ''}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="nieuwsbrief"
                  className="font-mono"
                  data-testid="form-input-slug"
                />
                {data.slug && (
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">/forms/{data.slug}</p>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs">Korte beschrijving (intern)</Label>
              <Input
                value={data.description || ''}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="Wat is dit formulier voor?"
                data-testid="form-input-description"
              />
            </div>
            <div>
              <Label className="text-xs">Intro tekst (zichtbaar op publieke pagina)</Label>
              <Textarea
                value={data.intro_text || ''}
                onChange={(e) => set({ intro_text: e.target.value })}
                rows={3}
                placeholder="Tekst die boven het formulier verschijnt"
                data-testid="form-input-intro"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Knop tekst</Label>
                <Input
                  value={data.submit_button_label || ''}
                  onChange={(e) => set({ submit_button_label: e.target.value })}
                  placeholder="Verzenden"
                  data-testid="form-input-button"
                />
              </div>
              <div className="flex items-end gap-2 pt-5">
                <Switch
                  checked={!!data.active}
                  onCheckedChange={(v) => set({ active: v })}
                  data-testid="form-input-active"
                />
                <Label className="text-xs">Formulier actief (zichtbaar op /forms/{data.slug || 'slug'})</Label>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Fields */}
        {tab === 'fields' && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground">
                Voeg velden toe en versleep ze met de pijltjes om de volgorde te wijzigen.
                Eén veld moet van type <code>email</code> zijn — dat wordt gebruikt om de lead aan te maken.
              </p>
              <div className="flex gap-1.5 flex-wrap">
                <select
                  onChange={(e) => { if (e.target.value) { addField(e.target.value); e.target.value = ''; } }}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  data-testid="form-add-field-select"
                  defaultValue=""
                >
                  <option value="" disabled>+ Veld toevoegen</option>
                  {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {(!data.fields || data.fields.length === 0) ? (
              <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                <p className="text-sm text-muted-foreground">Nog geen velden — voeg er minstens één toe.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.fields.map((f, idx) => (
                  <FieldEditor
                    key={idx}
                    field={f}
                    onChange={(patch) => updateField(idx, patch)}
                    onRemove={() => removeField(idx)}
                    onMoveUp={() => moveField(idx, -1)}
                    onMoveDown={() => moveField(idx, +1)}
                    canMoveUp={idx > 0}
                    canMoveDown={idx < data.fields.length - 1}
                    index={idx}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Submit acties */}
        {tab === 'submit' && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Lead bron (source)</Label>
                <Input
                  value={data.lead_source || ''}
                  onChange={(e) => set({ lead_source: e.target.value })}
                  placeholder={`form_${data.slug || 'slug'}`}
                  className="font-mono"
                  data-testid="form-input-lead-source"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Hoe leads in de CRM gemarkeerd worden. Leeg = automatisch <code>form_{data.slug || 'slug'}</code>.</p>
              </div>
              <div>
                <Label className="text-xs">Lead start-fase</Label>
                <Input
                  value={data.lead_stage || ''}
                  onChange={(e) => set({ lead_stage: e.target.value })}
                  placeholder="nieuw"
                  className="font-mono"
                  data-testid="form-input-lead-stage"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Lead tag / label (optioneel)</Label>
              <Input
                value={data.lead_tag || ''}
                onChange={(e) => set({ lead_tag: e.target.value })}
                placeholder="bijv. newsletter_subscriber"
                data-testid="form-input-lead-tag"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Wordt toegevoegd aan de lead-notitie.</p>
            </div>
            <div>
              <Label className="text-xs">Success bericht</Label>
              <Textarea
                value={data.success_message || ''}
                onChange={(e) => set({ success_message: e.target.value })}
                rows={2}
                data-testid="form-input-success"
              />
            </div>
            <div>
              <Label className="text-xs">Redirect URL na submit (optioneel)</Label>
              <Input
                value={data.redirect_url || ''}
                onChange={(e) => set({ redirect_url: e.target.value })}
                placeholder="https://example.com/bedankt"
                data-testid="form-input-redirect"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Als ingevuld wordt het success-bericht overgeslagen en stuurt de pagina door.</p>
            </div>
            <div>
              <Label className="text-xs">Notificatie e-mails (komma-gescheiden, optioneel)</Label>
              <Input
                value={(data.notify_emails || []).join(', ')}
                onChange={(e) => set({ notify_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="sales@bedrijf.nl, info@bedrijf.nl"
                data-testid="form-input-notify"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Wie krijgt er een melding bij nieuwe inzending (toekomstige functie).</p>
            </div>
          </div>
        )}

        {/* Tab: Branding */}
        {tab === 'branding' && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Accent kleur</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input
                    type="color"
                    value={data.accent_color || '#F97316'}
                    onChange={(e) => set({ accent_color: e.target.value })}
                    className="h-9 w-14 rounded border border-input cursor-pointer"
                    data-testid="form-input-accent"
                  />
                  <Input
                    value={data.accent_color || ''}
                    onChange={(e) => set({ accent_color: e.target.value })}
                    className="font-mono"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2 pt-5">
                <Switch
                  checked={data.show_logo !== false}
                  onCheckedChange={(v) => set({ show_logo: v })}
                  data-testid="form-input-show-logo"
                />
                <Label className="text-xs">Zynq logo tonen</Label>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="text-[11px] font-medium text-muted-foreground mb-2">Preview accent</p>
              <div className="flex items-center gap-3">
                <div className="h-1.5 w-32 rounded" style={{ backgroundColor: data.accent_color }} />
                <Button
                  size="sm"
                  className="text-white pointer-events-none"
                  style={{ backgroundColor: data.accent_color, borderColor: data.accent_color }}
                >
                  {data.submit_button_label || 'Verzenden'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuleren</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="form-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? 'Opslaan...' : (isNew ? 'Aanmaken' : 'Opslaan')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Single field editor row ───
function FieldEditor({ field, onChange, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, index }) {
  const hasOptions = TYPES_WITH_OPTIONS.has(field.type);

  const updateOption = (i, patch) => {
    const opts = [...(field.options || [])];
    opts[i] = { ...opts[i], ...patch };
    onChange({ options: opts });
  };
  const addOption = () => onChange({ options: [...(field.options || []), { label: `Optie ${(field.options || []).length + 1}`, value: `option_${(field.options || []).length + 1}` }] });
  const removeOption = (i) => onChange({ options: field.options.filter((_, j) => j !== i) });

  return (
    <div className="border border-border rounded-lg p-3 bg-card space-y-2" data-testid={`field-editor-${index}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-0.5 pt-2">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none"
            title="Omhoog"
          >▲</button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none"
            title="Omlaag"
          >▼</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 flex-1">
          <div className="sm:col-span-4">
            <Label className="text-[10px]">Label</Label>
            <Input value={field.label || ''} onChange={(e) => onChange({ label: e.target.value })} className="h-8 text-xs" />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-[10px]">Sleutel</Label>
            <Input
              value={field.key || ''}
              onChange={(e) => onChange({ key: keyify(e.target.value) })}
              className="h-8 text-xs font-mono"
            />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-[10px]">Type</Label>
            <select
              value={field.type}
              onChange={(e) => onChange({
                type: e.target.value,
                options: TYPES_WITH_OPTIONS.has(e.target.value)
                  ? (field.options && field.options.length ? field.options : [{ label: 'Optie 1', value: 'option_1' }])
                  : [],
              })}
              className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
            >
              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 flex items-end gap-1.5">
            <Switch checked={!!field.required} onCheckedChange={(v) => onChange({ required: v })} />
            <Label className="text-[10px]">Verplicht</Label>
          </div>
        </div>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={onRemove}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-6">
        <div>
          <Label className="text-[10px]">Placeholder</Label>
          <Input value={field.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-[10px]">Hulptekst (klein, onder veld)</Label>
          <Input value={field.help_text || ''} onChange={(e) => onChange({ help_text: e.target.value })} className="h-8 text-xs" />
        </div>
      </div>
      {hasOptions && (
        <div className="ml-6 space-y-1.5 pt-1">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] font-medium">Opties</Label>
            <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={addOption}>
              <Plus className="w-2.5 h-2.5" /> Optie
            </Button>
          </div>
          {(field.options || []).map((opt, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Input
                value={opt.label || ''}
                onChange={(e) => updateOption(i, { label: e.target.value, value: opt.value || keyify(e.target.value) })}
                placeholder="Label"
                className="h-7 text-xs flex-1"
              />
              <Input
                value={opt.value || ''}
                onChange={(e) => updateOption(i, { value: keyify(e.target.value) })}
                placeholder="value"
                className="h-7 text-xs w-32 font-mono"
              />
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeOption(i)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
